import { campaignInput } from '@trafiku/contracts';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { FormActions } from '@/components/form-actions';
import { ResourceList } from '@/components/resource-list';
import { Field, Input, Switch, TagInput, Textarea } from '@/components/ui/form';
import { Badge, Card, PageHeader, Spinner } from '@/components/ui/layout';
import { useFormState } from '@/lib/form';
import { formatDate } from '@/lib/format';
import { t, tn } from '@/lib/i18n';
import { useItem, useSave } from '@/lib/resources';
import type { Campaign } from '@/lib/types';

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export function CampaignsPage() {
  return (
    <ResourceList<Campaign>
      title={t('Campaigns')}
      subtitle={t(
        "An advertiser's campaign groups its station ads and map pins. Pausing a campaign pauses all of its ads.",
      )}
      resource="campaigns"
      basePath="/campaigns"
      newLabel={t('New campaign')}
      columns={[
        { header: t('Campaign'), cell: (c) => <span className="font-medium">{c.name}</span> },
        { header: t('Contact'), cell: (c) => c.contact },
        {
          header: t('Daily report'),
          cell: (c) =>
            c.notify && c.emails.length ? tn(c.emails.length, '{n} recipient', '{n} recipients') : t('Off'),
        },
        { header: t('Created'), cell: (c) => formatDate(c.created_date) },
        {
          header: t('Status'),
          cell: (c) =>
            c.is_active ? <Badge tone="green">{t('Running')}</Badge> : <Badge>{t('Paused')}</Badge>,
        },
      ]}
    />
  );
}

export function CampaignEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const item = useItem<Campaign>('campaigns', id);
  const save = useSave<Campaign>('campaigns', id);
  const navigate = useNavigate();
  const form = useFormState({
    name: '',
    description: '',
    contact: '',
    emails: [] as string[],
    notify: false,
    is_active: false,
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per item
  useEffect(() => {
    const c = item.data;
    if (c)
      form.setValues({
        name: c.name,
        description: c.description ?? '',
        contact: c.contact,
        emails: c.emails,
        notify: c.notify,
        is_active: c.is_active,
      });
  }, [item.data]);
  if (!isNew && item.isLoading) return <Spinner />;
  const { values, set, errors } = form;
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (await form.submit(campaignInput, (d) => save.mutateAsync(d))) navigate('/campaigns');
      }}
    >
      <PageHeader title={isNew ? t('New campaign') : values.name} back="/campaigns" />
      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        <Card>
          <div className="space-y-4">
            <Field label={t('Name')} error={errors.name}>
              {(fid) => (
                <Input
                  id={fid}
                  value={values.name}
                  onChange={(e) => set('name', e.target.value)}
                  invalid={!!errors.name}
                />
              )}
            </Field>
            <Field label={t('Contact')} error={errors.contact} hint={t('Advertiser name, phone or e-mail')}>
              {(fid) => (
                <Input
                  id={fid}
                  value={values.contact}
                  onChange={(e) => set('contact', e.target.value)}
                  invalid={!!errors.contact}
                />
              )}
            </Field>
            <Field label={t('Description')}>
              {(fid) => (
                <Textarea
                  id={fid}
                  value={values.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              )}
            </Field>
            <Field
              label={t('Report recipients')}
              error={errors.emails}
              hint={t("Receive yesterday's views and clicks every morning at 09:00.")}
            >
              {() => (
                <TagInput
                  values={values.emails}
                  onChange={(v) => set('emails', v)}
                  validate={isEmail}
                  placeholder="name@company.com"
                />
              )}
            </Field>
            <div className="flex flex-col gap-3">
              <Switch
                checked={values.notify}
                onChange={(v) => set('notify', v)}
                label={t('Send the daily report')}
              />
              <Switch
                checked={values.is_active}
                onChange={(v) => set('is_active', v)}
                label={t('Running (switching also starts/pauses all ads)')}
              />
            </div>
          </div>
          <div className="mt-6">
            <FormActions
              saving={save.isPending}
              resource="campaigns"
              id={id}
              backTo="/campaigns"
              deleteMessage={t('Campaigns that still have ads cannot be deleted.')}
            />
          </div>
        </Card>
        {!isNew && item.data && (
          <div className="space-y-4">
            <Card
              title={t('Station ads')}
              actions={
                <Link className="text-sm text-brand-700 hover:underline" to="/advertisements/new">
                  {t('Add')}
                </Link>
              }
            >
              <AdList
                items={(item.data.advertisements ?? []).map((a) => ({
                  _id: a._id,
                  name: a.title,
                  active: a.is_active,
                  views: a.views_count,
                  clicks: a.clicks_count,
                  to: `/advertisements/${a._id}`,
                }))}
              />
            </Card>
            <Card
              title={t('Map pins')}
              actions={
                <Link className="text-sm text-brand-700 hover:underline" to="/pin-advertisements/new">
                  {t('Add')}
                </Link>
              }
            >
              <AdList
                items={(item.data.pinadvertisements ?? []).map((a) => ({
                  _id: a._id,
                  name: a.name,
                  active: a.is_active,
                  views: a.views_count,
                  clicks: a.clicks_count,
                  to: `/pin-advertisements/${a._id}`,
                }))}
              />
            </Card>
          </div>
        )}
      </div>
    </form>
  );
}

function AdList({
  items,
}: {
  items: Array<{ _id: string; name: string; active: boolean; views: number; clicks: number; to: string }>;
}) {
  if (!items.length) return <p className="text-sm text-slate-500">{t('None yet')}</p>;
  return (
    <ul className="divide-y divide-slate-100 text-sm">
      {items.map((a) => (
        <li key={a._id} className="flex items-center justify-between py-2">
          <Link to={a.to} className="font-medium hover:underline">
            {a.name}
          </Link>
          <span className="flex items-center gap-3 text-xs text-slate-500">
            {t('{views} views · {clicks} clicks', { views: a.views, clicks: a.clicks })}{' '}
            {a.active ? <Badge tone="green">{t('On')}</Badge> : <Badge>{t('Off')}</Badge>}
          </span>
        </li>
      ))}
    </ul>
  );
}
